import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next, once } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { DSData, RequestError, Service } from 'webapp-types';
import { CompareFunction } from 'webapp/enums/enums';
import { AppEvent } from 'webapp/enums/events';
import { copyObject, utf8_to_b64 } from 'webapp/utils/common';
import { getDateRangeIdFromFormula } from 'webapp/utils/date-range';

import didUpdate from 'webapp/modifiers/did-update';
import willDestroy from 'webapp/modifiers/will-destroy';
import BarLoaderComponent from 'webapp/components/bar-loader';
import DataStatusIconComponent from 'webapp/components/data-status-icon';
import type { GoalInsightsChartState } from 'webapp/components/goals/insights/data-visualization';
import type { MetricInsightsChartState } from 'webapp/components/metric-details/explore/data';

import type { MetricSetup } from 'webapp/models-custom';
import type DataSelect from 'webapp/models-custom/data-select';
import type DateRange from 'webapp/models-custom/date-range';
import DsCompare from 'webapp/models-custom/ds-compare';
import type DChartPrimaryPoint from 'webapp/d-chart/point/primary-point';

interface Args {
	chartState: MetricInsightsChartState | GoalInsightsChartState;
	metricSetup: MetricSetup;
	dateRange: DateRange | null;
	forceLoadingState: boolean;
}

export default class GoalsInsightsAdditionalBoxesPreviousPeriodComponent extends Component<Args> {
	@service('model-manager/data-selects')
	declare mmDataSelectsService: Service.ModelManager.DataSelects;
	@service('model-manager/goals') declare mmGoalsService: Service.ModelManager.Goals;
	@service('billing') declare billingService: Service.Billing;
	@service('model-manager/samples') declare mmSamplesService: Service.ModelManager.Samples;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('request') declare requestService: Service.Request;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('metric-details') declare metricDetailsService: Service.MetricDetails;

	private paramHash = '';
	@tracked isLoadingDataQuery = false;
	@tracked dataSelect: DataSelect | null = null;

	private dataQueryXHR: JQuery.jqXHR<unknown> | null = null;

	constructor(owner: Owner, args: Args) {
		super(owner, args);
		this.recreateDataQuery();

		this.eventManagerService.on(AppEvent.METRIC_INSIGHTS_METRIC_EDITED, this, () => {
			next(() => {
				this.recreateDataQuery(true);
			});
		});
	}

	get isLoading() {
		return this.isLoadingDataQuery || this.args.forceLoadingState;
	}

	get canFetchLastYear() {
		const activePlan = this.billingService.activePlan,
			historicalDataRetention = activePlan?.features.historicalDataRetention as number | null;

		return !!activePlan && (historicalDataRetention === null || historicalDataRetention >= 12);
	}

	get points() {
		const points: DChartPrimaryPoint[] = [];
		this.dataSelect?.outputs?.forEach((output) => {
			const p = this.mmSamplesService.createDChartPointsFromOutput(output);
			points.push(...p);
		});
		return points;
	}

	get primaryPoint(): DChartPrimaryPoint | null {
		const points = this.points;

		return points[0] || null;
	}

	get comparePoint() {
		const primaryPoint = this.primaryPoint;

		return primaryPoint?.comparePoints[0] || null;
	}

	get hasPastValue() {
		return !!this.comparePoint?.hasValue;
	}

	get lastSyncFromData() {
		return this.dataSelect?.dataRoot?.lastSync ?? null;
	}

	get dataErrorStatusObjects() {
		return this.dataSelect?.dataRoot?.statusObjects;
	}

	get showDataStatusIcon(): boolean {
		const lastSync = !!this.lastSyncFromData,
			statusObjectsLength = this.dataErrorStatusObjects?.length || 0;

		return lastSync || statusObjectsLength > 0;
	}

	setData(data: { previewData: DSData.RootTop | null; paramHash: string }) {
		if (data.paramHash === this.paramHash) {
			this.dataSelect?.setFakeData(data.previewData);
			this.isLoadingDataQuery = false;
		}
	}

	requestDataQuery(dataSelect: DataSelect, paramHash: string) {
		// if request already in progress, cancel it
		if (this.dataQueryXHR && this.dataQueryXHR.readyState !== 4) {
			this.dataQueryXHR.abort();
		}

		const promise = this.mmDataSelectsService.fetchData(dataSelect, true);

		promise
			.then((previewData: DSData.RootTop) => {
				this.setData({
					previewData: previewData,
					paramHash,
				});
			})
			.catch((errorData: RequestError) => {
				this.setData({
					previewData: null,
					paramHash,
				});
				this.requestService.handleGenericThrownError(errorData);
			});

		this.dataQueryXHR = (promise.jqPromise as JQuery.jqXHR) ?? null;
	}

	private recreateDataQuery(forceNewRequest = false) {
		next(() => {
			// Create Chart Block and DS, push to store
			next(() => {
				// DS can be read from store
				const dataSelectData =
					this.args.chartState.block?.blockSetup?.firstShownDataSelect?.toJSON(true); // The chart component validates the metric selection and date range, just use the DS if it exists here

				if (dataSelectData) {
					const rangeOnlyFormula = copyObject(this.args.dateRange?.formula)!;
					delete rangeOnlyFormula.granulation;

					const dateRangeId = getDateRangeIdFromFormula(rangeOnlyFormula);

					const newParamHash = utf8_to_b64(
						[JSON.stringify(this.args.metricSetup.metricSetupData), dateRangeId].join(
							'',
						),
					);

					if (forceNewRequest || this.paramHash !== newParamHash) {
						this.isLoadingDataQuery = true;
						this.dataSelect?.setFakeData(null);

						this.paramHash = newParamHash;

						dataSelectData.id = this.mmGoalsService.generateGoalInsightsUUID();
						dataSelectData.blockId = null;
						const granulation = dataSelectData.dateRange!.granulation!; // a data select always has a granulation
						granulation.isWholeRange = true;
						granulation.timeUnit = null;
						granulation.multiplier = null;
						delete dataSelectData.compares;

						next(() => {
							const dataSelectModel =
									this.mmDataSelectsService.createNewModel(dataSelectData),
								dateRange = dataSelectModel.limitations.dateRange?.createCopy();

							if (dateRange && !dateRange.isAllTime) {
								const canFetchLastYear = this.canFetchLastYear,
									compareTypeId = canFetchLastYear
										? DsCompare.customPreviousOffsetYears
										: DsCompare.customPreviousOffsetMonths,
									offset = canFetchLastYear ? -1 : -6;

								const newCompare = DsCompare.createFromFormula(null);
								newCompare.updateFromTypeId(compareTypeId, dateRange);
								newCompare.offset = offset;
								newCompare.function = CompareFunction.ABSOLUTE;
								dataSelectModel.changeCompare(
									dataSelectModel.limitations.compareCMs[0]!,
									newCompare,
								);

								this.requestDataQuery(dataSelectModel, newParamHash);
							} else {
								this.isLoadingDataQuery = false;
							}

							this.unloadDataSelect();
							this.dataSelect = dataSelectModel;
						});
					}
				} else {
					this.paramHash = '';
				}
			});
		});
	}

	@action
	refreshDataQuery() {
		this.isLoadingDataQuery = true;

		next(() => {
			next(() => {
				this.isLoadingDataQuery = false; // To coincide with runloops in recreateDataQuery
			});
		});

		once(this, this.recreateDataQuery);
	}

	@action
	teardownView(): void {
		// Use dom event, not willDestroy cause user might quickly navigate through visited routes
		this.eventManagerService.off(AppEvent.METRIC_INSIGHTS_METRIC_EDITED, this);
	}

	unloadDataSelect() {
		if (this.dataSelect) {
			this.dataSelect.model.unloadRecord();
		}
	}

	override willDestroy() {
		super.willDestroy();
		this.dataQueryXHR?.abort();
		this.unloadDataSelect();
	}
	<template>
		<div
			class="module-box"
			{{didUpdate
				this.refreshDataQuery
				@metricSetup.key
				@metricSetup.dimensions
				@metricSetup.filtersData
				@dateRange.formulaId
				@forceLoadingState
			}}
			{{willDestroy this.teardownView}}
		>
			<div class="module-box__title">
				Same period
				{{#if this.canFetchLastYear}}1 year{{else}}6 months{{/if}}
				ago
				{{#if this.showDataStatusIcon}}
					<DataStatusIconComponent
						@context="metric"
						@lastSync={{this.lastSyncFromData}}
						@statusObjects={{this.dataErrorStatusObjects}}
						@theme={{this.navigationService.theme.id}}
					/>
				{{/if}}
			</div>

			{{#if this.isLoading}}
				<div class="module-box__loader">
					<BarLoaderComponent @size="tiny" />
				</div>
			{{else if this.hasPastValue}}
				<div class="module-box__value">
					{{this.comparePoint.valueStr}}
				</div>
			{{else}}
				<div class="module-box__value no-data">Not available</div>
			{{/if}}
		</div>
	</template>
}
